import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText, Search } from 'lucide-react';
import { formatLongDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import {
  Alert,
  Card,
  CardContent,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  type PageMeta,
} from '@/components/ui';

interface AuditRow {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  summary: string | null;
  actor: string;
  actorEmail: string | null;
  ipAddress: string | null;
  requestId: string | null;
  previousValue: unknown;
  newValue: unknown;
  createdAt: string;
}

/** Actions worth filtering by, ordered roughly by how often they are looked up. */
const ACTIONS = [
  'LOAN_APPROVED',
  'LOAN_REJECTED',
  'LOAN_DISBURSED',
  'REPAYMENT_RECORDED',
  'REPAYMENT_REVERSED',
  'KYC_APPROVED',
  'KYC_REJECTED',
  'STATUS_CHANGE',
  'DOCUMENT_DOWNLOADED',
  'EXPORT_GENERATED',
  'LOGIN',
  'LOGIN_FAILED',
  'PASSWORD_CHANGED',
  'SETTINGS_CHANGED',
  'CREATE',
  'UPDATE',
  'DELETE',
];

/** Tone by how much scrutiny an action deserves when scanning the log. */
function toneFor(action: string): string {
  if (/REVERSED|REJECTED|FAILED|DELETE|DEFAULT/.test(action)) return 'text-danger';
  if (/APPROVED|DISBURSED|RECORDED/.test(action)) return 'text-success';
  if (/DOWNLOADED|EXPORT|SETTINGS|PASSWORD/.test(action)) return 'text-warning';
  return 'text-foreground-secondary';
}

export default function AdminAuditLog() {
  const [query, setQuery] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', page, query, action],
    queryFn: () =>
      api.get<{ data: AuditRow[]; meta: PageMeta }>(
        `/audit-logs?pageSize=25&page=${page}${query ? `&q=${encodeURIComponent(query)}` : ''}${
          action ? `&action=${action}` : ''
        }`,
      ),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Audit trail"
        description={data ? `${data.meta.total} recorded actions` : undefined}
      />

      <Alert tone="info" className="mb-6">
        The audit trail is append-only. Entries are written inside the same transaction as the
        change they describe, and there is no endpoint anywhere in the system that can edit or
        delete one.
      </Alert>

      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <Field label="Search" htmlFor="q" className="min-w-[240px] flex-1">
            <div className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-secondary"
              />
              <Input
                id="q"
                className="pl-9"
                placeholder="Search the summary, e.g. a loan number"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              />
            </div>
          </Field>

          <Field label="Action" htmlFor="action" className="w-56">
            <Select id="action" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}>
              <option value="">All actions</option>
              {ACTIONS.map((option) => (
                <option key={option} value={option}>
                  {option.replace(/_/g, ' ').toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data || data.data.length === 0 ? (
        <EmptyState icon={<ScrollText size={20} />} title="No matching entries" />
      ) : (
        <div className="space-y-2">
          {data.data.map((entry) => {
            const open = expanded === entry.id;
            const hasDetail = Boolean(entry.previousValue || entry.newValue);

            return (
              <Card key={entry.id} className="overflow-hidden">
                <button
                  type="button"
                  className="w-full px-5 py-4 text-left transition-colors hover:bg-muted/50"
                  onClick={() => setExpanded(open ? null : entry.id)}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`font-mono text-xs font-bold uppercase ${toneFor(entry.action)}`}
                        >
                          {entry.action.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-foreground-secondary">on {entry.entity}</span>
                      </div>
                      <p className="mt-1 text-sm text-foreground">
                        {entry.summary ?? '(no summary)'}
                      </p>
                      <p className="mt-1 text-xs text-foreground-secondary">
                        {entry.actor}
                        {entry.actorEmail ? ` · ${entry.actorEmail}` : ''}
                        {entry.ipAddress ? ` · ${entry.ipAddress}` : ''}
                      </p>
                    </div>
                    <p className="numeric shrink-0 text-xs text-foreground-secondary">
                      {formatLongDate(new Date(entry.createdAt))}
                      <br />
                      {new Date(entry.createdAt).toLocaleTimeString('en-NG')}
                    </p>
                  </div>
                </button>

                {open && hasDetail && (
                  <div className="grid gap-4 border-t border-border bg-muted/40 p-5 sm:grid-cols-2">
                    <div>
                      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-foreground-secondary">
                        Before
                      </p>
                      <pre className="overflow-x-auto rounded-lg bg-background p-3 text-xs text-foreground">
                        {JSON.stringify(entry.previousValue, null, 2) ?? '—'}
                      </pre>
                    </div>
                    <div>
                      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-foreground-secondary">
                        After
                      </p>
                      <pre className="overflow-x-auto rounded-lg bg-background p-3 text-xs text-foreground">
                        {JSON.stringify(entry.newValue, null, 2) ?? '—'}
                      </pre>
                    </div>
                    {entry.requestId && (
                      <p className="numeric col-span-full text-xs text-foreground-secondary">
                        Request ID: {entry.requestId}
                      </p>
                    )}
                  </div>
                )}
              </Card>
            );
          })}

          <Card>
            <Pagination meta={data.meta} onPageChange={setPage} className="border-t-0" />
          </Card>
        </div>
      )}
    </div>
  );
}
